import { useNavigate } from 'react-router';
import { ArrowRight, CalendarDays, ScanSearch, Store } from 'lucide-react';
import { DialogDescription, DialogTitle } from '@accio3d/design-system/dialog';
import icon from '@accio3d/shared-ui/assets/arcessio-icon.svg';
import { setSidebarOpen } from '../agent/agent-sidebar-store';
import { SupplierOnboardingDialog } from './supplier-onboarding-dialog';

const cardClassName =
  'group flex flex-col items-start rounded-2xl border bg-card/80 p-5 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-6';

/** Lets accepted suppliers choose their first action in Arcessio. */
export function SupplierNextStepsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();

  /** Closes onboarding and opens the selected supplier screen. */
  function openPage(path: string) {
    onOpenChange(false);
    navigate(path);
  }

  /** Opens Arcessio on the current screen after closing onboarding. */
  function askArcessio() {
    onOpenChange(false);
    setSidebarOpen(true);
  }

  return (
    <SupplierOnboardingDialog open={open} onOpenChange={onOpenChange} step={2}>
      <DialogTitle className="text-2xl font-semibold tracking-tight sm:text-3xl">
        Welcome to Arcessio
      </DialogTitle>
      <DialogDescription className="mt-2 text-base">
        What would you like to do first?
      </DialogDescription>
      <div className="my-7 grid gap-4 sm:grid-cols-3">
        {[
          {
            Icon: Store,
            title: 'Create your storefront',
            description: 'Add your capabilities, equipment, certifications, and company profile.',
            action: 'Set up storefront',
            path: '/storefront',
          },
          {
            Icon: ScanSearch,
            title: 'Analyze a part',
            description: 'Upload a CAD file or drawing and let Arcessio analyze manufacturability.',
            action: 'Analyze a part',
            path: '/parts/import',
          },
        ].map(({ Icon, title, description, action, path }) => (
          <button key={path} type="button" className={cardClassName} onClick={() => openPage(path)}>
            <Icon className="mb-5 h-6 w-6 text-primary" />
            <span className="text-base font-semibold">{title}</span>
            <span className="mb-6 mt-2 text-sm leading-6 text-muted-foreground">{description}</span>
            <span className="mt-auto flex items-center gap-2 text-sm font-semibold text-primary">
              {action} <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        ))}
        <a
          className={cardClassName}
          href="https://calendly.com/mm-accio3d/30min"
          target="_blank"
          rel="noopener noreferrer"
        >
          <CalendarDays className="mb-5 h-6 w-6 text-primary" />
          <span className="text-base font-semibold">Meet the Arcessio team</span>
          <span className="mb-6 mt-2 text-sm leading-6 text-muted-foreground">
            See how the platform works and how to get the most from your account.
          </span>
          <span className="mt-auto flex items-center gap-2 text-sm font-semibold text-primary">
            Book a demo <ArrowRight className="h-4 w-4" />
          </span>
        </a>
      </div>
      <button
        type="button"
        onClick={askArcessio}
        className="flex items-center gap-2 rounded-md text-left text-sm text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <img src={icon} alt="" className="h-4 w-4" />
        Not sure where to start? Ask Arcessio <ArrowRight className="h-4 w-4 shrink-0" />
      </button>
      <p className="mt-6 text-xs leading-6 text-muted-foreground">
        Tip: click the Arcessio icon anywhere in Arcessio to ask a question about what you’re
        looking at.
      </p>
    </SupplierOnboardingDialog>
  );
}
